import { useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import { Placeholder } from "@tiptap/extensions";
import { Bold, Heading, Highlighter, Italic, Link2, Link2Off, List, ListOrdered, Underline as UnderlineIcon } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import "./announcement.css";

function ToolButton({ active, onClick, title, testid, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-pressed={!!active}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
        active ? "bg-[#1B4F8A] text-white" : "text-stone-600 hover:bg-stone-200 hover:text-slate-900"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function LinkBar({ editor, onClose }) {
  const [url, setUrl] = useState(editor.getAttributes("link").href || "");
  const apply = () => {
    const v = url.trim();
    if (v) editor.chain().focus().extendMarkRange("link").setLink({ href: /^(https?:\/\/|mailto:)/i.test(v) ? v : `https://${v}` }).run();
    onClose();
  };
  return (
    <div className="flex items-center gap-2 border-t border-stone-200 bg-stone-50 px-2 py-2" data-testid="ann-link-bar">
      <Input
        autoFocus
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); apply(); }
          if (e.key === "Escape") onClose();
        }}
        placeholder="https://www.medicys.co.uk"
        className="h-8 text-sm"
        data-testid="ann-link-input"
      />
      <Button type="button" size="sm" onClick={apply} className="h-8 bg-[#1B4F8A] hover:bg-[#173f6e]" data-testid="ann-link-apply">Apply</Button>
      <Button type="button" size="sm" variant="ghost" onClick={onClose} className="h-8" data-testid="ann-link-cancel">Cancel</Button>
    </div>
  );
}

export function RichTextEditor({ value, onChange, placeholder, testid = "announcement-body-input" }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [, bump] = useState(0);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        link: { openOnClick: false, autolink: true, defaultProtocol: "https", HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } },
        blockquote: false, codeBlock: false, code: false, horizontalRule: false, strike: false,
      }),
      Highlight,
      Placeholder.configure({ placeholder: placeholder || "" }),
    ],
    content: value || "",
    editorProps: { attributes: { class: "ann-rich ann-rich--light", "data-testid": testid } },
    onUpdate: ({ editor: ed }) => onChange({ html: ed.isEmpty ? "" : ed.getHTML(), text: ed.getText({ blockSeparator: "\n" }) }),
    onSelectionUpdate: () => bump((n) => n + 1),
    onTransaction: () => bump((n) => n + 1),
  });

  if (!editor) return null;

  const toggleLink = () => {
    if (editor.isActive("link")) editor.chain().focus().unsetLink().run();
    else setLinkOpen(true);
  };

  return (
    <div className="ann-editor rounded-md border border-stone-300 bg-white focus-within:ring-2 focus-within:ring-[#1B4F8A]/30" data-testid="ann-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-stone-200 bg-stone-50 px-1.5 py-1" data-testid="ann-toolbar">
        <ToolButton title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testid="ann-tool-bold"><Bold className="w-4 h-4" /></ToolButton>
        <ToolButton title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testid="ann-tool-italic"><Italic className="w-4 h-4" /></ToolButton>
        <ToolButton title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} testid="ann-tool-underline"><UnderlineIcon className="w-4 h-4" /></ToolButton>
        <ToolButton title="Highlight in Medicys blue" active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()} testid="ann-tool-highlight"><Highlighter className="w-4 h-4" /></ToolButton>
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <ToolButton title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} testid="ann-tool-heading"><Heading className="w-4 h-4" /></ToolButton>
        <ToolButton title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testid="ann-tool-bullets"><List className="w-4 h-4" /></ToolButton>
        <ToolButton title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testid="ann-tool-numbers"><ListOrdered className="w-4 h-4" /></ToolButton>
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <ToolButton title={editor.isActive("link") ? "Remove link" : "Add link"} active={editor.isActive("link")} onClick={toggleLink} testid="ann-tool-link">
          {editor.isActive("link") ? <Link2Off className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </ToolButton>
      </div>
      <EditorContent editor={editor} />
      {linkOpen && <LinkBar editor={editor} onClose={() => { setLinkOpen(false); editor.commands.focus(); }} />}
    </div>
  );
}
